import type { CSSProperties } from "react";

export type MarketingIconName =
  | "calendar"
  | "check-circle"
  | "checklist"
  | "chat"
  | "clock"
  | "document"
  | "email"
  | "flag"
  | "globe"
  | "graduation"
  | "heart"
  | "lightning"
  | "lock"
  | "magnifier"
  | "map"
  | "mindset"
  | "microphone"
  | "music"
  | "people"
  | "play"
  | "progress"
  | "question"
  | "quote"
  | "shield"
  | "star"
  | "tag"
  | "target"
  | "television"
  | "tension"
  | "trophy"
  | "user-target"
  | "video"
  | "waveform";

type MarketingIconBadgeProps = {
  icon: MarketingIconName;
  className?: string;
  iconClassName?: string;
  variant?: "dark" | "light" | "soft";
};

export function MarketingIcon({ icon, className = "h-7 w-7" }: { icon: MarketingIconName; className?: string }) {
  const strokeWidth = 1.9;

  if (icon === "check-circle") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="m8.4 12.1 2.3 2.3 4.9-5.2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "calendar") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M7 4.4v3M17 4.4v3M5.2 9h13.6M6.4 6.1h11.2c.9 0 1.7.8 1.7 1.7v10.3c0 .9-.8 1.7-1.7 1.7H6.4c-.9 0-1.7-.8-1.7-1.7V7.8c0-.9.8-1.7 1.7-1.7Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M8.2 12.4h2.1M13.7 12.4h2.1M8.2 15.8h2.1M13.7 15.8h2.1" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "checklist") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M9.6 5.4h8.2M9.6 12h8.2M9.6 18.6h8.2" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
        <path d="m4.8 5.4.9.9 1.8-2M4.8 12l.9.9 1.8-2M4.8 18.6l.9.9 1.8-2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "chat") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M5.2 6.6c0-1 .8-1.8 1.8-1.8h10c1 0 1.8.8 1.8 1.8v7c0 1-.8 1.8-1.8 1.8h-4.4L8.2 19v-3.6H7c-1 0-1.8-.8-1.8-1.8v-7Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M8.7 10.1h.01M12 10.1h.01M15.3 10.1h.01" stroke="currentColor" strokeLinecap="round" strokeWidth="2.6" />
      </svg>
    );
  }

  if (icon === "clock") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="M12 7.2v5.1l3.4 2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "document") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M6.7 3.9h7.1l3.7 3.7v12.5H6.7V3.9Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M13.6 4.2v3.8h3.7M9.2 11.2h5.6M9.2 14.6h5.6M9.2 18h3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "email") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <rect x="4.6" y="6.3" width="14.8" height="11.4" rx="2" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="m5.5 7.4 6.5 5 6.5-5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="m9.7 11.9-4.3 4M14.3 11.9l4.3 4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "globe") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="M3.9 12h16.2M12 3.8c2.2 2.1 3.4 4.8 3.4 8.2S14.2 18.1 12 20.2C9.8 18.1 8.6 15.4 8.6 12S9.8 5.9 12 3.8Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "flag") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M6.4 20.2V4.2M6.4 5.2c2.8-1.3 5.2 1.2 8 0 1.2-.5 2.2-.8 3.2-.7v8.2c-1-.1-2 .2-3.2.7-2.8 1.2-5.2-1.3-8 0" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "graduation") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M3.8 9.3 12 4.8l8.2 4.5-8.2 4.5-8.2-4.5Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M7.2 11.2v4.4c1.2 1.6 2.8 2.4 4.8 2.4s3.6-.8 4.8-2.4v-4.4M19.6 10.1v5.7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "heart") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M12 20.1c-4.4-3.5-7.5-6.2-7.5-9.8A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7.5 2.3c0 3.6-3.1 6.3-7.5 9.8Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "lock") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M7.2 10.4V8.2a4.8 4.8 0 0 1 9.6 0v2.2" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
        <rect x="5.2" y="10.4" width="13.6" height="9" rx="2" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="M12 14.2v1.7" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "lightning") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="m13.2 3.8-6 9.3h4.2l-.6 7.1 6.3-9.6h-4.4l.5-6.8Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "microphone") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <rect x="8.6" y="3.4" width="6.8" height="10.2" rx="3.4" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="M5.6 10.4a6.4 6.4 0 0 0 12.8 0M12 16.8v3.8M8.8 20.6h6.4" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "mindset") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M8.2 19.4v-3.1c-1.5-1.4-2.3-3.2-2.3-5.2 0-4 3.2-7.2 7.2-7.2 3.6 0 6.5 2.6 7.1 6 .2 1.1.8 2.1 1.6 2.8l.7.6c.5.4.2 1.2-.5 1.2h-2.2v2.4c0 .9-.7 1.6-1.6 1.6h-3.3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M13.1 13.9c-2-1.5-3.3-2.8-3.3-4.4 0-1 .8-1.8 1.8-1.8.6 0 1.1.3 1.5.8.4-.5.9-.8 1.5-.8 1 0 1.8.8 1.8 1.8 0 1.6-1.3 2.9-3.3 4.4Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "magnifier") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <circle cx="10.4" cy="10.4" r="6.4" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="m15.2 15.2 4.7 4.7" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "map") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="m4.6 6.8 4.8-2.1 5.2 2.1 4.8-2.1v12.5l-4.8 2.1-5.2-2.1-4.8 2.1V6.8Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M9.4 4.7v12.5M14.6 6.8v12.5" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "music") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M9 17.3V5.8l9-1.8v11.4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <circle cx="6.8" cy="17.5" r="2.2" stroke="currentColor" strokeWidth={strokeWidth} />
        <circle cx="15.8" cy="15.6" r="2.2" stroke="currentColor" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "people") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <circle cx="9" cy="8.4" r="3" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="M3.8 19.2c.7-3.1 2.6-4.7 5.2-4.7s4.5 1.6 5.2 4.7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M15 11.2a2.7 2.7 0 1 0-1-5.2M14.8 14.7c2.6.1 4.4 1.6 5.1 4.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "progress") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M4.2 18.8h3.5v-3.2h3.5v-3.3h3.6V9.2h2.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M17.6 9.2V4.8c1.8-.9 3.2.8 4.7 0v3.9c-1.5.8-2.9-.9-4.7.5Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M17.6 9.2v3.2M4.2 18.8h15.7" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "play") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="m10.2 8.7 5.4 3.3-5.4 3.3V8.7Z" fill="currentColor" />
      </svg>
    );
  }

  if (icon === "quote") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M9.7 7.4c-2.5 1.2-3.9 3.2-3.9 5.9v3.3h5.3v-5.1H8.7c.1-1.4.8-2.5 2.1-3.3L9.7 7.4ZM17.2 7.4c-2.5 1.2-3.9 3.2-3.9 5.9v3.3h5.3v-5.1h-2.4c.1-1.4.8-2.5 2.1-3.3l-1.1-.8Z" fill="currentColor" />
      </svg>
    );
  }

  if (icon === "question") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.2" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="M9.9 9.4a2.3 2.3 0 0 1 4.4 1c0 1.8-2.1 2-2.1 3.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M12.2 16.9h.01" stroke="currentColor" strokeLinecap="round" strokeWidth="2.8" />
      </svg>
    );
  }

  if (icon === "shield") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M12 3.6 18.5 6v4.8c0 4.2-2.5 7.6-6.5 9.2-4-1.6-6.5-5-6.5-9.2V6L12 3.6Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="m9.2 11.7 1.8 1.8 3.8-4.3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "star") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="m12 3.8 2.3 4.7 5.2.8-3.8 3.7.9 5.2-4.6-2.5-4.6 2.5.9-5.2-3.8-3.7 5.2-.8L12 3.8Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "target") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <circle cx="11.2" cy="12.8" r="6.2" stroke="currentColor" strokeWidth={strokeWidth} />
        <circle cx="11.2" cy="12.8" r="2.4" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="M15.6 8.4 19 5M19 5v3.5M19 5h-3.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "user-target") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <circle cx="10.1" cy="8.7" r="2.9" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="M4.9 18.8c.8-3.4 2.7-5.1 5.2-5.1 1.8 0 3.2.8 4.2 2.4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <circle cx="17.1" cy="12.2" r="3.2" stroke="currentColor" strokeWidth={strokeWidth} />
        <circle cx="17.1" cy="12.2" r=".7" fill="currentColor" />
        <path d="M19.3 9.9 21 8.2M21 8.2v1.8M21 8.2h-1.8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "tension") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M8.5 5.2c.5 2.1 1 3.8 2.4 4.9M15.5 5.2c-.5 2.1-1 3.8-2.4 4.9M9.5 13.3c1.3.8 3.7.8 5 0M8.3 17.8c2.1 1.3 5.3 1.3 7.4 0" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <path d="M4.6 10.4 2.9 8.8M4 14H2M19.4 10.4l1.7-1.6M20 14h2" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "television") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <rect x="4.2" y="6.5" width="15.6" height="10.4" rx="1.8" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="m9.2 3.8 2.8 2.7 2.8-2.7M9.2 20.2h5.6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "tag") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M4.8 12.4 11.7 5.5h6.1v6.1l-6.9 6.9a2 2 0 0 1-2.8 0l-3.3-3.3a2 2 0 0 1 0-2.8Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
        <circle cx="15.8" cy="7.8" r="1.1" fill="currentColor" />
      </svg>
    );
  }

  if (icon === "video") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <rect x="4.2" y="6.2" width="11.4" height="11.6" rx="2" stroke="currentColor" strokeWidth={strokeWidth} />
        <path d="m15.6 10.1 4.2-2.4v8.6l-4.2-2.4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (icon === "waveform") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path d="M3.8 12h1.4M7.7 15.8V8.2M11 18.2V5.8M14.3 15.8V8.2M18.8 12h1.4" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
        <path d="M17.4 14.2V9.8" stroke="currentColor" strokeLinecap="round" strokeWidth={strokeWidth} />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M8.4 20.2h7.2M12 16.8v3.4M6.6 4.8h10.8v4.5a5.4 5.4 0 0 1-10.8 0V4.8Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
      <path d="M6.6 7.2H4.3v1.3a3.6 3.6 0 0 0 3.6 3.6M17.4 7.2h2.3v1.3a3.6 3.6 0 0 1-3.6 3.6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} />
    </svg>
  );
}

export function MarketingIconBadge({
  icon,
  className = "",
  iconClassName,
  variant = "dark",
}: MarketingIconBadgeProps) {
  const style: CSSProperties =
    variant === "soft"
      ? {
          background: "linear-gradient(135deg, #fffdf8 0%, #eef5f1 100%)",
          borderColor: "rgba(217, 199, 143, 0.58)",
          color: "#d9a22c",
        }
      : variant === "light"
      ? {
          background: "#ffffff",
          borderColor: "rgba(221, 213, 197, 0.92)",
          color: "#d9a22c",
        }
      : {
          background: "radial-gradient(circle at 34% 24%, rgba(255,255,255,0.22), transparent 1.8rem), #123b34",
          borderColor: "rgba(217, 199, 143, 0.52)",
          color: "#e2b43e",
        };

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full border shadow-[0_18px_36px_rgba(18,33,31,0.18),inset_0_1px_0_rgba(255,255,255,0.16)] ${className}`}
      style={style}
      aria-hidden="true"
    >
      <MarketingIcon icon={icon} className={iconClassName} />
    </span>
  );
}
